import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '@/redux/store';
import { fetchUnreadChatCount } from '@/api/chatApi';

interface ChatBadgeProps {
    className?: string;
}

const ChatBadge: React.FC<ChatBadgeProps> = ({ className = "" }) => {
    const [count, setCount] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(true);
    const user = useSelector((state: RootState) => state.user.user);

    const loadChatCount = async () => {
        if (!user?.id) {
            setCount(0);
            setLoading(false);
            return;
        }

        try {
            const chatCount = await fetchUnreadChatCount();
            setCount(chatCount);
        } catch (error) {
            console.error('Error fetching chat count:', error);
            setCount(0);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadChatCount();
    }, [user?.id]);

    // Listen for custom events to refresh the count
    useEffect(() => {
        const handleChatChange = () => {
            loadChatCount();
        };

        // Listen for custom events when chats are updated
        window.addEventListener('chatUpdated', handleChatChange);
        window.addEventListener('messageSent', handleChatChange);

        return () => {
            window.removeEventListener('chatUpdated', handleChatChange);
            window.removeEventListener('messageSent', handleChatChange);
        };
    }, [user?.id]);

    if (loading) {
        return null;
    }

    if (count === 0) {
        return null;
    }

    return (
        <div className={`absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ${className}`}>
            {count > 99 ? '99+' : count}
        </div>
    );
};

export default ChatBadge;

